import { Box, useTheme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import moment from 'moment'
import React from 'react'
import {
  CalendarDayIcon,
  DraftMessageIcon,
  HqOverdueIcon,
  PendingDocIcon,
  TagIcon,
  UnreadMessageIcon,
} from 'src/designSystem/icons'
import { PatientTag } from 'src/graphql.generated'
import MeruDate from 'src/legacy/lib/MeruDate'
import { HomeViewParticipantFragment } from '../searchParticipants.generated'
import CallFollowUpIndicator from './CallFollowUpIndicator'
import CallTodayIndicator from './CallTodayIndicator'
import DaysDisengagedIndicator from './DaysDisengagedIndicator'
import IndicatorChip from './IndicatorChip'
import RiskIndicator from './RiskIndicator'
import { calendarDayLabel } from './calendarDayLabel'
import { shouldShowCallTodayIndicator } from './callTodayUtils'
import type { DraftMessage } from './useDraftMessage'

type Props = {
  expanded: boolean
  draftMessage: DraftMessage | null
  indicators?: HomeViewParticipantFragment['indicators']
  tags?: Array<PatientTag> | null
  programStartDate?: Date | null
  showDayIndicator?: boolean
  timezone?: string | null
}

const useStyles = makeStyles((theme) => ({
  cluster: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
}))

const PriorityIndicators = (props: Props) => {
  const { expanded, draftMessage, indicators, tags, programStartDate, showDayIndicator, timezone } = props

  const styles = useStyles()
  const { palette } = useTheme()

  const dayInWeekNbr = MeruDate.now(timezone).formatWeekDayNumber(programStartDate)
  const risk = indicators?.risk
  const daysDisengaged = indicators?.daysDisengaged
  const currentOrUpcomingCall = indicators?.currentOrUpcomingCall
  const completedCallAwaitingNote = indicators?.completedCallAwaitingNote
  const newMessagesCount = indicators?.newMessages?.count

  return (
    <Box className={styles.cluster}>
      {showDayIndicator && dayInWeekNbr != null && (
        <IndicatorChip tooltip={calendarDayLabel(dayInWeekNbr)} testId="day-indicator">
          <CalendarDayIcon day={dayInWeekNbr} color={palette.darkestGreen} />
        </IndicatorChip>
      )}

      {tags && tags.length > 0 && (
        <IndicatorChip tooltip={tags.map((tag) => tag.name).join(', ')} testId="tag-indicator">
          <TagIcon />
        </IndicatorChip>
      )}

      {risk && <RiskIndicator level={risk.level} trend={risk.trend} expanded={expanded} />}

      {daysDisengaged && (
        <DaysDisengagedIndicator days={daysDisengaged.days} level={daysDisengaged.level} expanded={expanded} />
      )}

      {indicators?.overdueHealthQuestionnaire && (
        <IndicatorChip tooltip="HQ overdue" label={expanded ? 'HQ overdue' : undefined} testId="hq-overdue-indicator">
          <HqOverdueIcon />
        </IndicatorChip>
      )}

      {indicators?.monthlySummaryNotes?.showIndicator && (
        <IndicatorChip
          tooltip="Pending monthly summary doc"
          label={expanded ? 'Pending doc' : undefined}
          testId="pending-doc-indicator"
        >
          <PendingDocIcon />
        </IndicatorChip>
      )}

      {completedCallAwaitingNote && (
        <CallFollowUpIndicator
          callType={completedCallAwaitingNote.callType}
          startTime={completedCallAwaitingNote.startTime}
          expanded={expanded}
        />
      )}

      {currentOrUpcomingCall && shouldShowCallTodayIndicator(currentOrUpcomingCall) && (
        <CallTodayIndicator
          callType={currentOrUpcomingCall.callType}
          startTime={currentOrUpcomingCall.startTime}
          expanded={expanded}
        />
      )}

      {!!newMessagesCount && (
        <IndicatorChip
          tooltip={`${newMessagesCount} unread ${newMessagesCount === 1 ? 'message' : 'messages'}`}
          color={palette.darkestGreen}
          testId="unread-message-indicator"
        >
          <UnreadMessageIcon count={newMessagesCount} color={palette.darkestGreen} />
        </IndicatorChip>
      )}

      {draftMessage && (
        <IndicatorChip
          tooltip={`Draft expires in ${moment(draftMessage.expiresAt).diff(moment(), 'hours')} hours`}
          testId="draft-message-indicator"
        >
          <DraftMessageIcon />
        </IndicatorChip>
      )}
    </Box>
  )
}

export default PriorityIndicators
